import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Link, useLoaderData } from "react-router-dom";
import { deleteEquipmentGroup, getEquipmentGroups, getTools, saveEquipmentGroup } from "../../../data/loca/tools";
import { notify, send } from "../../../services/NotificationService";

// Matériels partagés : un même matériel physique publié sous plusieurs annonces (ex. multi-outil : débroussailleuse,
// taille-haie, élagueuse). Ses annonces partagent le stock — louer l'une rend les autres indisponibles sur la
// période — et leurs statistiques (historique, répartition, satisfaction, top du tableau de bord) sont cumulées.

export async function loader() {
    const [groups, tools] = await Promise.all([getEquipmentGroups(), getTools()]);
    return { groups: groups ?? [], tools: tools ?? [] };
}

const normalize = (text) => (text ?? "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

const toolLabel = (tool) => {
    const brandModel = [tool.brand, tool.model].filter(Boolean).join(" ");
    return brandModel ? `${tool.name} — ${brandModel}` : tool.name;
};

// Annonces non regroupées de même modèle (et même marque) : probablement un seul matériel physique.
function buildSuggestions(tools) {
    const byBrandModel = new Map();
    tools
        .filter((tool) => !tool.equipmentGroupId && normalize(tool.model))
        .forEach((tool) => {
            const key = `${normalize(tool.brand)}|${normalize(tool.model)}`;
            byBrandModel.set(key, [...(byBrandModel.get(key) ?? []), tool]);
        });

    return [...byBrandModel.values()]
        .filter((group) => group.length > 1)
        .map((group) => ({
            name: [group[0].brand, group[0].model].filter(Boolean).join(" "),
            tools: group.sort((a, b) => a.name.localeCompare(b.name))
        }));
}

/**
 * Création / modification d'un matériel partagé.
 * group : matériel existant ({ id, name, tools }) ou null ; initialToolPublicIds : annonces présélectionnées (création).
 * onSaved(savedGroup | null) : null quand le matériel a été dissous.
 */
export function EquipmentGroupEditor({ visible, onHide, group, initialToolPublicIds = [], initialName = "", tools, onSaved }) {
    const [name, setName] = useState("");
    const [selected, setSelected] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setName(group?.name ?? initialName ?? "");
        setSelected(group ? group.tools.map((tool) => tool.publicId) : initialToolPublicIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, group?.id]);

    const options = useMemo(() => (tools ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((tool) => ({
            value: tool.publicId,
            label: toolLabel(tool),
            // Annonce déjà rattachée à un autre matériel : elle le quittera à l'enregistrement.
            elsewhere: tool.equipmentGroupId && tool.equipmentGroupId !== group?.id
        })), [tools, group?.id]);

    const selectedTools = (tools ?? []).filter((tool) => selected.includes(tool.publicId));
    const sharedStock = selectedTools.length > 0 ? Math.min(...selectedTools.map((tool) => tool.stockTotal ?? 1)) : null;
    const movingTools = options.filter((option) => option.elsewhere && selected.includes(option.value));

    const save = async () => {
        if (selected.length < 2) {
            send("warning", "Choisissez au moins deux annonces.");
            notify();
            return;
        }
        setIsSaving(true);
        try {
            const saved = await saveEquipmentGroup(group?.id, name.trim() || null, selected);
            send("success", group ? "Matériel partagé mis à jour." : "Matériel partagé créé.");
            onSaved?.(saved);
            onHide();
        } catch (error) {
            send("error", error.message || "Enregistrement impossible.");
        } finally {
            setIsSaving(false);
            notify();
        }
    };

    const optionTemplate = (option) => (
        <div className="equipment-option">
            <span>{option.label}</span>
            {option.elsewhere && <small>déjà dans un autre matériel</small>}
        </div>
    );

    return (
        <Dialog
            header={group ? "Modifier le matériel partagé" : "Nouveau matériel partagé"}
            visible={visible}
            onHide={onHide}
            className="equipment-dialog"
            style={{ width: "min(560px, 94vw)" }}
            footer={(
                <div className="equipment-dialog__footer">
                    <Button label="Annuler" severity="secondary" text onClick={onHide} disabled={isSaving} />
                    <Button label="Enregistrer" icon="pi pi-check" onClick={save} loading={isSaving} disabled={isSaving} />
                </div>
            )}
        >
            <p className="equipment-dialog__intro">
                Regroupez les annonces qui correspondent à <b>une seule machine</b> (ex. un multi-outil proposé en débroussailleuse,
                taille-haie et élagueuse). Louer l'une les rend toutes indisponibles sur la période, et leurs statistiques sont cumulées.
            </p>
            <div className="equipment-dialog__field">
                <label htmlFor="equipment-name">Nom du matériel</label>
                <InputText id="equipment-name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Facultatif (ex. Multi-outil Vevor 52cc)" />
            </div>
            <div className="equipment-dialog__field">
                <label htmlFor="equipment-tools">Annonces de ce matériel</label>
                <MultiSelect inputId="equipment-tools" value={selected} onChange={(e) => setSelected(e.value)}
                    options={options} optionLabel="label" optionValue="value" itemTemplate={optionTemplate}
                    display="chip" filter filterPlaceholder="Rechercher une annonce" placeholder="Choisissez au moins deux annonces"
                    className="w-full" />
            </div>
            {sharedStock != null && selected.length > 1 && (
                <p className="equipment-dialog__hint">
                    <i className="pi pi-box"></i> Stock commun : <b>{sharedStock}</b> exemplaire{sharedStock > 1 ? "s" : ""} (le plus petit stock des annonces).
                    Une même location qui prend plusieurs de ces annonces compte pour une seule machine.
                </p>
            )}
            {movingTools.length > 0 && (
                <p className="equipment-dialog__hint equipment-dialog__hint--warning">
                    <i className="pi pi-exclamation-triangle"></i> {movingTools.map((tool) => tool.label).join(", ")} quitter{movingTools.length > 1 ? "ont" : "a"} son matériel partagé actuel.
                </p>
            )}
        </Dialog>
    );
}

export default function EquipmentGroupsPage() {
    const loaded = useLoaderData();
    const [groups, setGroups] = useState(loaded.groups);
    const [tools, setTools] = useState(loaded.tools);
    const [editor, setEditor] = useState({ visible: false, group: null, initialToolPublicIds: [], initialName: "" });

    const suggestions = useMemo(() => buildSuggestions(tools), [tools]);

    const reload = async () => {
        const [nextGroups, nextTools] = await Promise.all([getEquipmentGroups(), getTools()]);
        setGroups(nextGroups ?? []);
        setTools(nextTools ?? []);
    };

    const openEditor = (group, initialToolPublicIds = [], initialName = "") =>
        setEditor({ visible: true, group, initialToolPublicIds, initialName });

    const dissolve = (event, group) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Dissoudre ce matériel partagé ? Ses annonces redeviendront indépendantes (stock et statistiques séparés).",
            icon: "pi pi-info-circle",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            acceptLabel: "Oui",
            rejectLabel: "Non",
            accept: async () => {
                try {
                    await deleteEquipmentGroup(group.id);
                    send("success", "Matériel partagé dissous.");
                    await reload();
                } catch (error) {
                    send("error", error.message || "Suppression impossible.");
                }
                notify();
            }
        });
    };

    return (
        <div className="equipment-page">
            <ConfirmPopup />
            <div className="equipment-page__header">
                <div>
                    <h1>Matériels partagés</h1>
                    <p>
                        Une machine proposée sous plusieurs annonces (multi-outil, machine 2 en 1…) : quand l'une est louée,
                        les autres sont indisponibles sur la même période, et les statistiques sont cumulées.
                    </p>
                </div>
                <Button icon="pi pi-plus" label="Nouveau matériel partagé" onClick={() => openEditor(null)} />
            </div>

            {groups.length === 0 ? (
                <div className="equipment-empty">
                    <i className="pi pi-sitemap"></i>
                    <p>Aucun matériel partagé pour l'instant.</p>
                </div>
            ) : (
                <div className="equipment-grid">
                    {groups.map((group) => (
                        <article className="equipment-card" key={group.id}>
                            <header className="equipment-card__header">
                                <div>
                                    <h2>{group.displayName}</h2>
                                    <span className="equipment-card__stock">
                                        Stock commun : {group.stockTotal} · {group.tools.length} annonces
                                    </span>
                                </div>
                                <div className="equipment-card__actions">
                                    <Button icon="pi pi-pencil" text rounded aria-label="Modifier" onClick={() => openEditor(group)} />
                                    <Button icon="pi pi-trash" text rounded severity="danger" aria-label="Dissoudre" onClick={(e) => dissolve(e, group)} />
                                </div>
                            </header>
                            <ul className="equipment-card__tools">
                                {group.tools.map((tool) => (
                                    <li key={tool.publicId}>
                                        <Link to={`/monoutil/${tool.publicId}/details`}>
                                            {tool.base64Picture
                                                ? <img src={tool.base64Picture} alt="" />
                                                : <span className="equipment-card__nopic pi pi-image"></span>}
                                            <span>{tool.name}</span>
                                        </Link>
                                        {!tool.isActive && <small>inactive</small>}
                                        {tool.stockTotal !== group.stockTotal && <small>stock {tool.stockTotal}</small>}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            )}

            {suggestions.length > 0 && (
                <section className="equipment-suggestions">
                    <h2>Suggestions</h2>
                    <p>Ces annonces ont la même marque et le même modèle. S'il s'agit de la même machine, regroupez-les.</p>
                    {suggestions.map((suggestion) => (
                        <div className="equipment-suggestion" key={suggestion.name}>
                            <div>
                                <b>{suggestion.name}</b>
                                <span>{suggestion.tools.map((tool) => tool.name).join(", ")}</span>
                            </div>
                            <Button size="small" outlined icon="pi pi-link" label="Regrouper…"
                                onClick={() => openEditor(null, suggestion.tools.map((tool) => tool.publicId))} />
                        </div>
                    ))}
                </section>
            )}

            <EquipmentGroupEditor
                visible={editor.visible}
                onHide={() => setEditor((current) => ({ ...current, visible: false }))}
                group={editor.group}
                initialToolPublicIds={editor.initialToolPublicIds}
                initialName={editor.initialName}
                tools={tools}
                onSaved={reload}
            />
        </div>
    );
}
